import {
  Button,
  fontStyles,
  LoadingIndicator,
  Modal,
  ModalSection,
} from '@monash/portal-react';
import { useContext, useEffect, useRef, useState } from 'react';
import c from './personalisation-modal.module.scss';
import classNames from 'classnames';
import {
  AccessibilityContext,
  APIContext,
  useApi,
} from '@monash/portal-frontend-common';
import { DataContext } from '@/components/providers/data-provider/DataProvider';
import { useSnackbar } from '@/components/providers/SnackbarProvider';
import {
  buildPersonaCombination,
  trackPersonalisationReSubmit,
  trackPersonalisationSubmit,
} from '@/utils/analytics';
import * as Sentry from '@sentry/browser';

const MAX_ANSWERS_PER_QUESTION = 3;

const PersonalisationModal = ({
  open,
  onClose,
  isUpdate,
  existingQuestionResponses,
}) => {
  const { getLinksPersonalisationQuestions, updateLinksPersonalisation } =
    useContext(APIContext);
  const { resetAppLiveMsgs } = useContext(AccessibilityContext);
  const { setLinksPersonalisation } = useContext(DataContext);

  const { addSnackbar } = useSnackbar();

  const {
    data: linksPersonalisationQuestions,
    isLoading: isLoadingQuestions,
    call: fetchLinksPersonalisationQuestions,
  } = useApi({
    requestFn: getLinksPersonalisationQuestions,
    skipInitialCall: true,
    onError: (error) => {
      Sentry.captureException(error);
      resetAppLiveMsgs();
      addSnackbar({
        message:
          'We are unable to load personalisation questions right now - try again later',
        type: 'error',
      });
    },
  });

  useEffect(() => {
    if (open && !linksPersonalisationQuestions) {
      fetchLinksPersonalisationQuestions();
    }
  }, [open, linksPersonalisationQuestions, fetchLinksPersonalisationQuestions]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [questionResponses, setQuestionResponses] = useState({});
  const questionsRef = useRef([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const questionCount = linksPersonalisationQuestions?.length ?? 0;
  const showBackButton = currentIndex > 0;
  const showSubmitButton =
    questionCount > 0 && currentIndex === questionCount - 1;
  const questionResponsesValues = Object.values(questionResponses);
  const canSubmit =
    questionCount > 0 &&
    questionResponsesValues.length === questionCount &&
    questionResponsesValues.every((response) => response?.length > 0);

  const scrollToQuestion = (index) => {
    const target = questionsRef.current?.[index];
    target?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleAnswerSelect = ({ questionId, answerId }) => {
    setQuestionResponses((prev) => {
      const prevAnswersToQuestion = prev[questionId];
      const shouldSelect = !prevAnswersToQuestion?.includes(answerId);

      let newAnswers;

      if (shouldSelect) {
        const hasNoAnswer = !prevAnswersToQuestion?.length;
        const hasReachedMax =
          prevAnswersToQuestion?.length === MAX_ANSWERS_PER_QUESTION;
        if (hasNoAnswer) {
          newAnswers = [answerId];
        } else if (hasReachedMax) {
          newAnswers = [...prevAnswersToQuestion.slice(1), answerId];
        } else {
          newAnswers = [...prevAnswersToQuestion, answerId];
        }
      } else {
        newAnswers = prevAnswersToQuestion.filter((ans) => ans !== answerId);
      }

      return {
        ...prev,
        [questionId]: newAnswers,
      };
    });
  };

  const handleBack = () => {
    setCurrentIndex((prevIndex) => {
      if (prevIndex > 0) {
        const targetIndex = prevIndex - 1;
        scrollToQuestion(targetIndex);
        return targetIndex;
      }

      return prevIndex;
    });
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) => {
      if (prevIndex < questionCount - 1) {
        const targetIndex = prevIndex + 1;
        scrollToQuestion(targetIndex);
        return targetIndex;
      }

      return prevIndex;
    });
  };

  const handleClose = () => {
    onClose();
    setCurrentIndex(0);
    setQuestionResponses({});
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setIsSubmitting(true);
    try {
      const saved = await updateLinksPersonalisation({
        questionResponses,
      });

      setLinksPersonalisation(saved);

      const personaCombination = buildPersonaCombination(
        saved.questionResponses
      );
      const submittedAt = new Date(saved.responsesUpdatedAt).toISOString();

      if (isUpdate) {
        trackPersonalisationReSubmit({
          personaCombination,
          previousPersonaCombination: buildPersonaCombination(
            existingQuestionResponses
          ),
          submittedAt,
        });
      } else {
        trackPersonalisationSubmit({
          personaCombination,
          submittedAt,
        });
      }

      handleClose();
    } catch (error) {
      resetAppLiveMsgs();
      addSnackbar({
        message:
          'We are unable to save your personalisation results right now - try again later',
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} className={c.modal}>
      <ModalSection noPadding headerClassName={c.modalHeader}>
        {isLoadingQuestions ? (
          <div className={c.loading}>
            <LoadingIndicator color="var(--card-text-color)" />
          </div>
        ) : (
          <div className={c.personalisationContainer}>
            <div className={c.questions}>
              {linksPersonalisationQuestions?.map((question, i) => {
                const { id: questionId, text: questionText } = question;

                return (
                  <div
                    key={questionId}
                    ref={(node) => {
                      questionsRef.current[i] = node;
                    }}
                    className={c.questionContainer}
                  >
                    <div className={c.questionHeader}>
                      <div
                        className={classNames(
                          fontStyles.heading,
                          fontStyles.md
                        )}
                      >
                        {questionText}
                      </div>
                      <div>{`Select up to ${MAX_ANSWERS_PER_QUESTION} answers`}</div>
                    </div>
                    <div className={c.answers}>
                      {question.answers?.map((answer) => {
                        const { id: answerId, text: answerText } = answer;

                        return (
                          <button
                            key={answerId}
                            type="button"
                            className={classNames(c.answerButton, {
                              [c.selected]:
                                questionResponses[questionId]?.includes(
                                  answerId
                                ),
                            })}
                            onClick={() =>
                              handleAnswerSelect({
                                questionId,
                                answerId,
                              })
                            }
                          >
                            {answerText}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className={c.controls}>
              <div className={c.dots} inert>
                {Array.from({ length: questionCount }, (_, i) => (
                  <div
                    key={i}
                    className={classNames(c.dot, {
                      [c.current]: currentIndex === i,
                    })}
                  />
                ))}
              </div>
              <div className={c.buttons}>
                {showBackButton && (
                  <Button onClick={handleBack} variant="secondary">
                    Back
                  </Button>
                )}
                {showSubmitButton ? (
                  <Button
                    onClick={handleSubmit}
                    variant="primary"
                    disabled={!canSubmit}
                    loading={isSubmitting}
                  >
                    Submit
                  </Button>
                ) : (
                  <Button
                    onClick={handleNext}
                    variant="primary"
                    disabled={questionCount === 0}
                  >
                    Next
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </ModalSection>
    </Modal>
  );
};

export default PersonalisationModal;
